import React from 'react';

const PatrimonioMalProtegido: React.FC = () => {
  return (
    <section className="relative bg-gradient-to-br from-rose-50/50 via-white to-red-50/30 py-12 sm:py-16 lg:py-28 overflow-hidden">
      {/* Background pattern overlay - very subtle */}
      <div className="absolute inset-0 opacity-[0.025]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}
        ></div>
      </div>

      {/* Soft accent glows - very light */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-100/50 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-rose-100/40 rounded-full blur-3xl pointer-events-none"></div>

      <div className="container mx-auto px-5 sm:px-6 lg:px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center min-h-[500px] lg:min-h-[650px]">

          {/* ══════════════════════════════════════════════════════════ */}
          {/* Coluna Esquerda - Conteúdo Principal */}
          {/* ══════════════════════════════════════════════════════════ */}
          <div className="space-y-6 sm:space-y-8 lg:pr-8">

            {/* Badge Superior */}
            <div className="inline-flex items-center gap-2 sm:gap-2.5 bg-red-50 border border-red-200 rounded-full px-4 sm:px-5 py-2 sm:py-2.5">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse shrink-0"></div>
              <span className="text-red-700 font-bold text-xs sm:text-sm tracking-[0.15em] uppercase">
                Patrimônio Exposto
              </span>
            </div>

            {/* Título Principal */}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 leading-[1.15]">
              Sem a assessoria certa, seu patrimônio pode{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-500">
                continuar exposto mesmo com seguro contratado
              </span>
            </h2>

            {/* Texto Principal */}
            <div className="space-y-3 sm:space-y-4">
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                Ter seguro não significa, automaticamente, estar protegido da forma correta. Quando a contratação é feita sem critério técnico, o consumidor pode continuar vulnerável mesmo pagando pela apólice.
              </p>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                Isso acontece quando:
              </p>
            </div>

            {/* Lista de Problemas Comuns */}
            <div className="space-y-2.5 sm:space-y-3">
              {[
                {
                  icon: 'ri-alert-line',
                  text: 'A cobertura não acompanha o risco real'
                },
                {
                  icon: 'ri-shield-cross-line',
                  text: 'O patrimônio é segurado abaixo do necessário'
                },
                {
                  icon: 'ri-file-warning-line',
                  text: 'Existem exclusões contratuais mal compreendidas'
                },
                {
                  icon: 'ri-close-circle-line',
                  text: 'A estrutura da proteção não foi planejada estrategicamente'
                }
              ].map((item, index) => (
                <div
                  key={index}
                  className="group flex items-center gap-3 sm:gap-4 bg-white border border-slate-200 rounded-lg sm:rounded-xl p-3.5 sm:p-4 hover:border-red-300 hover:bg-red-50/30 transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 bg-red-50 rounded-lg flex items-center justify-center border border-red-200 group-hover:border-red-300 transition-colors">
                    <i className={`${item.icon} text-red-500 text-lg sm:text-xl`}></i>
                  </div>
                  <p className="text-slate-700 font-medium text-sm sm:text-base leading-relaxed flex-1">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Bloco de Autoridade IBX Brasil */}
            <div className="relative bg-white border border-slate-200 border-l-4 border-l-emerald-500 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-7">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 bg-emerald-50 rounded-lg sm:rounded-xl flex items-center justify-center border border-emerald-200">
                  <i className="ri-shield-star-line text-emerald-600 text-xl sm:text-2xl"></i>
                </div>
                <div>
                  <div className="text-emerald-700 font-bold text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2">
                    Solução IBX Brasil
                  </div>
                  <p className="text-slate-800 text-base sm:text-lg leading-relaxed font-semibold">
                    A IBX Brasil profissionaliza a contratação de seguros para garantir uma estrutura de proteção mais segura, eficiente e coerente com o patrimônio do cliente.
                  </p>
                </div>
              </div>
            </div>

            {/* Indicadores Visuais */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-1">
              {[
                { label: 'Mais segurança', desc: 'Proteção melhor estruturada' },
                { label: 'Mais clareza', desc: 'Menos falhas contratuais' },
                { label: 'Mais proteção', desc: 'Patrimônio resguardado com eficiência' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white border border-slate-200 rounded-lg sm:rounded-xl p-4 sm:p-5 hover:border-emerald-300 hover:bg-emerald-50/20 transition-all duration-300 group"
                >
                  <div className="text-emerald-700 font-bold text-xs sm:text-sm uppercase tracking-wider mb-1.5">
                    {item.label}
                  </div>
                  <div className="text-slate-500 text-xs leading-relaxed">
                    {item.desc}
                  </div>
                </div>
              ))}
            </div>

            {/* Destaque Final - Assinatura */}
            <div className="relative bg-slate-900 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-7">
              <div className="absolute -left-0 top-1/2 -translate-y-1/2 w-1 h-14 sm:h-18 bg-gradient-to-b from-red-500 to-orange-500 rounded-r"></div>
              <div className="flex items-start gap-3 sm:gap-4 pl-3">
                <i className="ri-double-quotes-l text-red-400/30 text-3xl sm:text-4xl flex-shrink-0 -mt-1"></i>
                <p className="text-white text-base sm:text-xl font-bold leading-relaxed">
                  O maior risco não é apenas o imprevisto.{' '}
                  <span className="text-red-300">É descobrir tarde demais que a proteção contratada não era suficiente.</span>
                </p>
              </div>
            </div>

            {/* CTA Principal */}
            <div className="pt-2 sm:pt-4">
              <a
                href="#analise-gratuita"
                className="group flex sm:inline-flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-action-orange-500 to-red-500 hover:from-action-orange-600 hover:to-red-600 text-white font-bold px-6 sm:px-8 lg:px-10 py-4 sm:py-5 rounded-xl transition-all duration-300 w-full sm:w-auto text-center"
              >
                <i className="ri-shield-check-line text-xl sm:text-2xl shrink-0"></i>
                <span className="text-sm sm:text-base lg:text-lg leading-tight">Quero proteger meu patrimônio com a IBX Brasil</span>
                <i className="ri-arrow-right-line text-lg sm:text-xl group-hover:translate-x-1 transition-transform shrink-0"></i>
              </a>
              <p className="text-slate-500 text-xs sm:text-sm mt-3 sm:mt-4 italic flex items-center justify-center sm:justify-start gap-2">
                <i className="ri-shield-star-line text-emerald-500"></i>
                Contratação mais segura, análise profissional e proteção estruturada.
              </p>
            </div>

          </div>

          {/* ══════════════════════════════════════════════════════════ */}
          {/* Coluna Direita - Imagem Dominante */}
          {/* ══════════════════════════════════════════════════════════ */}
          <div className="relative h-full min-h-[400px] sm:min-h-[550px] lg:min-h-[650px] mt-4 lg:mt-0">
            <div className="relative h-full rounded-xl sm:rounded-2xl overflow-hidden">

              {/* Imagem Principal */}
              <img
                src="https://readdy.ai/api/search-image?query=worried%20client%20or%20family%20analyzing%20important%20insurance%20contracts%20and%20documents%20with%20concerned%20expression%20in%20elegant%20premium%20corporate%20office%20environment%20professional%20business%20setting%20sophisticated%20atmosphere%20reviewing%20inadequate%20insurance%20coverage%20protection%20vulnerability%20strategic%20decision%20making%20simple%20elegant%20background%20high%20quality%20photography%20style%20serious%20contemplative%20mood%20modern%20workspace&width=900&height=1000&seq=patrimonio-mal-protegido-main-v1&orientation=portrait"
                alt="Cliente preocupado analisando contratos de seguros inadequados e proteção patrimonial insuficiente"
                className="w-full h-full object-cover"
                loading="lazy"
              />

              {/* Overlay gradiente leve */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent"></div>

              {/* Badge superior direito - Estatística de alerta */}
              <div className="absolute top-4 sm:top-6 right-4 sm:right-6 bg-red-600 text-white rounded-lg sm:rounded-xl px-4 sm:px-5 py-3 sm:py-4 border border-red-500/50">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                  <i className="ri-shield-cross-fill text-base sm:text-lg"></i>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider opacity-90">Risco</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold leading-none">67%</div>
                <div className="text-[10px] sm:text-xs font-medium opacity-90 whitespace-nowrap">segurados mal protegidos</div>
              </div>

              {/* Card Sobreposto na Imagem - Parte inferior */}
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-200">
                <div className="flex items-start gap-3 sm:gap-5">
                  <div className="flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-red-600 to-orange-600 rounded-lg sm:rounded-xl flex items-center justify-center">
                    <i className="ri-shield-cross-line text-white text-xl sm:text-2xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2 flex-wrap">
                      <h4 className="text-slate-900 font-bold text-base sm:text-lg">Proteção inadequada</h4>
                      <i className="ri-error-warning-fill text-red-600 text-base sm:text-lg"></i>
                    </div>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      Sem análise técnica, falhas só aparecem quando o patrimônio já está em risco
                    </p>
                  </div>
                </div>

                {/* Mini indicadores dentro do card */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-slate-100">
                  {[
                    { icon: 'ri-shield-cross-line', text: 'Cobertura baixa' },
                    { icon: 'ri-file-warning-line', text: 'Falhas contratuais' },
                    { icon: 'ri-alert-line', text: 'Patrimônio exposto' }
                  ].map((badge, idx) => (
                    <div key={idx} className="text-center">
                      <i className={`${badge.icon} text-red-500 text-base sm:text-lg mb-1 block`}></i>
                      <span className="text-[9px] sm:text-[10px] text-slate-600 font-medium uppercase tracking-wide block leading-tight">
                        {badge.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default PatrimonioMalProtegido;
